/**
 * URL generation utilities for public API links
 */

import { PUBLIC_API_ROUTES } from '@loader/shared-types';

import type { ExportFormat, ResourceType } from '@/features/links/types';
import { API_BASE_URL } from '@/config/constants';

/**
 * Generate an OData URL for Power BI / Excel Power Query integration (tables)
 *
 * Note: No API key in URL - Power BI/Excel should use Basic Auth (email + API key)
 *
 * @param tableRef The table ID or name
 * @param versionRef Version reference ('current', 'previous', tag, or version ID) - defaults to 'current'
 * @returns The OData feed URL (service document with single "data" entity set)
 */
export function generateODataUrl(tableRef: string, versionRef: string = 'current'): string {
  const path = PUBLIC_API_ROUTES.odata.perTable.serviceDocument(tableRef, versionRef);
  return `${API_BASE_URL}${path}`;
}

/**
 * Encode version selections as a path segment for linked table OData URLs.
 * Format: alias1=V1,alias2=current,alias3=V2
 *
 * @param versionSelections Map of table alias to version (e.g., { 'orders': 'V1', 'customers': 'current' })
 * @returns Encoded version config string (without the @ prefix)
 */
export function encodeVersionConfig(versionSelections: Record<string, string>): string {
  const entries = Object.entries(versionSelections);
  if (entries.length === 0) return '';

  return entries
    .map(([alias, version]) => `${encodeURIComponent(alias)}=${encodeURIComponent(version)}`)
    .join(',');
}

/**
 * Generate an OData URL for linked tables with version config encoded in path.
 * This is designed for Power BI / Excel Power Query which strip query parameters.
 *
 * URL pattern: /api/public/linked-tables/:linkedTableRef/odata/@alias1=current,alias2=<uuid>/
 *
 * Version selections are ALWAYS included in the URL for explicit, predictable behavior.
 * Values can be: 'current', 'previous', or a version UUID.
 *
 * Note: No API key in URL - Power BI/Excel should use Basic Auth (email + API key)
 *
 * @param linkedTableRef The linked table UUID
 * @param versionSelections Map of table alias to version (e.g., { 'orders': 'current', 'customers': '<uuid>' })
 * @returns The OData feed URL
 */
export function generateLinkedTableODataUrl(
  linkedTableRef: string,
  versionSelections?: Record<string, string>,
  variableParams?: Record<string, string>
): string {
  let path: string;

  // Always include version config in URL for explicit, predictable behavior
  if (versionSelections && Object.keys(versionSelections).length > 0) {
    const versionConfig = encodeVersionConfig(versionSelections);
    path = PUBLIC_API_ROUTES.odata.linkedTable.serviceDocumentWithVersions(linkedTableRef, versionConfig);
  } else {
    // No version selections provided - use base URL (will default to 'current' on backend)
    path = PUBLIC_API_ROUTES.odata.linkedTable.serviceDocument(linkedTableRef);
  }

  let url = `${API_BASE_URL}${path}`;

  // Append variable params as query string
  if (variableParams) {
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(variableParams)) {
      if (value !== '') {
        params.append(`params[${name}]`, value);
      }
    }
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return url;
}

/**
 * Generate a public data insert URL for post_api connectors.
 *
 * @param tableRef The table ID or name
 * @returns The POST endpoint URL for inserting JSON data
 */
export function generateDataInsertUrl(tableRef: string): string {
  const path = PUBLIC_API_ROUTES.dataInsert(tableRef);
  return `${API_BASE_URL}${path}`;
}

/**
 * Get the file extension for a given format
 */
export function getFormatExtension(format: ExportFormat): string {
  switch (format) {
    case 'csv':
      return '.csv';
    case 'xlsx':
      return '.xlsx';
    case 'xml':
      return '.xml';
    case 'json':
    default:
      return '.json';
  }
}

/**
 * Generate a public API URL for a resource
 * @param resourceId The ID of the resource (table or linkedTable)
 * @param resourceType The type of the resource ('table' or 'linkedTable')
 * @param format The desired export format ('json', 'csv', or 'xlsx')
 * @param versionRef Optional. The version reference for tables ('current', 'previous', tag, or version ID)
 * @param publicKey Optional. The public API key to include in the URL
 * @param versionSelections Optional. Version selections for linked tables (keyed by table alias)
 * @returns The generated public API URL with key parameter if provided
 */
export function generatePublicApiUrl(
  resourceId: string,
  resourceType: ResourceType,
  format: ExportFormat,
  versionRef?: string,
  publicKey?: string | null,
  versionSelections?: Record<string, string>,
  variableParams?: Record<string, string>
): string {
  let path: string;

  if (resourceType === 'linkedTable') {
    // Use PUBLIC_API_ROUTES for linked tables
    switch (format) {
      case 'csv':
        path = PUBLIC_API_ROUTES.linkedTables.csv(resourceId);
        break;
      case 'xlsx':
        path = PUBLIC_API_ROUTES.linkedTables.xlsx(resourceId);
        break;
      case 'xml':
        path = PUBLIC_API_ROUTES.linkedTables.xml(resourceId);
        break;
      default:
        path = PUBLIC_API_ROUTES.linkedTables.json(resourceId);
    }
  } else {
    // Table - use PUBLIC_API_ROUTES
    const version = versionRef || 'current';
    switch (format) {
      case 'csv':
        path = PUBLIC_API_ROUTES.tables.csv(resourceId, version);
        break;
      case 'xlsx':
        path = PUBLIC_API_ROUTES.tables.xlsx(resourceId, version);
        break;
      case 'xml':
        path = PUBLIC_API_ROUTES.tables.xml(resourceId, version);
        break;
      default:
        path = PUBLIC_API_ROUTES.tables.json(resourceId, version);
    }
  }

  // Build full URL with base
  let url = `${API_BASE_URL}${path}`;

  // Build query parameters
  const params = new URLSearchParams();

  // Append key parameter if provided
  if (publicKey) {
    params.append('key', publicKey);
  }

  // For JSON format, default to raw mode (returns array without metadata wrapper)
  // This is better for PowerQuery/Excel and most integrations
  if (format === 'json') {
    params.append('raw', 'true');
  }

  // Append version selections for linked tables
  if (resourceType === 'linkedTable' && versionSelections) {
    for (const [alias, versionId] of Object.entries(versionSelections)) {
      // Only add non-'current' selections (current is the default)
      if (versionId && versionId !== 'current') {
        params.append(`table_versions[${alias}]`, versionId);
      }
    }
  }

  // Append variable params for linked tables
  if (resourceType === 'linkedTable' && variableParams) {
    for (const [name, value] of Object.entries(variableParams)) {
      if (value !== '') {
        params.append(`params[${name}]`, value);
      }
    }
  }

  // Append query string if we have any parameters
  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}

